import React, { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, NodeResizer } from '@xyflow/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Layers, Edit3, Check, X, Trash2 } from 'lucide-react';
import { AnalysisCluster } from '@/services/analysis';

interface ClusterNodeData extends AnalysisCluster {
  onUpdateCluster?: (clusterId: string, updates: Partial<AnalysisCluster>) => void;
  onDeleteCluster?: (clusterId: string) => void;
  onRemoveNoteFromCluster?: (noteId: string, clusterId: string) => void;
}

export const ClusterNode = memo(({ id, data }: NodeProps) => {
  const clusterData = data as unknown as ClusterNodeData;
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(clusterData.name);
  const [editDescription, setEditDescription] = useState(clusterData.description || '');

  const handleSave = useCallback(() => {
    if (clusterData.onUpdateCluster && editName.trim()) {
      clusterData.onUpdateCluster(id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      setIsEditing(false);
    }
  }, [id, editName, editDescription, clusterData]);

  const handleCancel = useCallback(() => {
    setEditName(clusterData.name);
    setEditDescription(clusterData.description || '');
    setIsEditing(false);
  }, [clusterData.name, clusterData.description]);

  const handleDelete = useCallback(() => {
    if (clusterData.onDeleteCluster) {
      clusterData.onDeleteCluster(id);
    }
  }, [id, clusterData]);

  return (
    <>
      <NodeResizer minWidth={250} minHeight={150} />
      <Card 
        className="w-full h-full p-4 border-2 border-dashed border-amber-300 bg-amber-50/50 backdrop-blur-sm"
        style={{ 
          width: clusterData.width || 300,
          height: clusterData.height || 200 
        }}
      >
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 !bg-amber-400 !border-2 !border-white"
        />
        
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-amber-600" />
            <Badge variant="secondary" className="text-xs">
              Theme Cluster
            </Badge>
          </div>
          <div className="flex items-center gap-1">
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="w-6 h-6 p-0 hover:bg-amber-100"
                onClick={() => setIsEditing(true)}
              >
                <Edit3 className="w-3 h-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-6 h-6 p-0 hover:bg-red-100 text-red-600"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="space-y-3 flex-1">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Theme name..."
              className="text-sm font-medium"
            />
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Describe this theme or pattern..."
              className="text-xs resize-none"
              rows={3}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={!editName.trim()}>
                <Check className="w-3 h-3 mr-1" />
                Save
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800 mb-2 text-sm">
              {clusterData.name}
            </h3>
            {clusterData.description && (
              <p className="text-xs text-amber-700 mb-3 opacity-80">
                {clusterData.description}
              </p>
            )}
            <div className="absolute bottom-4 left-4 right-4">
              <Badge variant="outline" className="text-xs bg-white/50">
                {clusterData.note_ids.length} notes
              </Badge>
            </div>
          </div>
        )}

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 !bg-amber-400 !border-2 !border-white"
        />
      </Card>
    </>
  );
});

ClusterNode.displayName = 'ClusterNode';